import { useState, useRef, useCallback, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface Suggestion {
  label: string;    // display text e.g. "trigger.event"
  insert: string;   // text to insert e.g. "trigger.event}}"
  description?: string;
}

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: Suggestion[];
  multiline?: boolean;
  className?: string;
  placeholder?: string;
  spellCheck?: boolean;
  /** Plain mode: autocomplete on any text (no {{ trigger needed).
   *  Suggestions are matched against the full input value. */
  plainMode?: boolean;
  /** Called when Enter is pressed and autocomplete menu is NOT open. */
  onSubmit?: () => void;
}

export function AutocompleteInput({
  value,
  onChange,
  suggestions,
  multiline = false,
  className = "",
  placeholder,
  spellCheck,
  plainMode = false,
  onSubmit,
}: AutocompleteInputProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Find the start of the current {{ token at cursor position
  const getTokenStart = useCallback(
    (text: string, cursor: number): number | null => {
      if (plainMode) {
        // In plain mode, the whole input is the expression
        return text.length > 0 ? 0 : null;
      }
      // Look backwards from cursor for {{
      const before = text.slice(0, cursor);
      const lastOpen = before.lastIndexOf("{{");
      if (lastOpen === -1) return null;
      // Make sure there's no }} between {{ and cursor
      const between = before.slice(lastOpen + 2);
      if (between.includes("}}")) return null;
      return lastOpen;
    },
    [plainMode]
  );

  const filtered = suggestions.filter((s) =>
    s.label.toLowerCase().includes(filter.toLowerCase())
  );

  const handleChange = useCallback(
    (newValue: string) => {
      onChange(newValue);
      // Defer cursor check to after state update
      setTimeout(() => {
        const el = ref.current;
        if (!el) return;
        const cursor = el.selectionStart ?? newValue.length;
        setCursorPos(cursor);
        const tokenStart = getTokenStart(newValue, cursor);
        if (tokenStart !== null) {
          const partial = plainMode
            ? newValue // whole value is the expression
            : newValue.slice(tokenStart + 2, cursor);
          setFilter(partial);
          setShowMenu(true);
          setSelectedIdx(0);
        } else {
          setShowMenu(false);
        }
      }, 0);
    },
    [onChange, getTokenStart, plainMode]
  );

  const insertSuggestion = useCallback(
    (suggestion: Suggestion) => {
      const el = ref.current;
      if (!el) return;

      if (plainMode) {
        // Replace entire input with the suggestion label (the path)
        onChange(suggestion.label);
        setShowMenu(false);
        setTimeout(() => {
          el.setSelectionRange(suggestion.label.length, suggestion.label.length);
          el.focus();
        }, 0);
        return;
      }

      const cursor = el.selectionStart ?? value.length;
      const tokenStart = getTokenStart(value, cursor);
      if (tokenStart === null) return;

      // Replace from {{ to cursor with {{insert}}
      const before = value.slice(0, tokenStart);
      const after = value.slice(cursor);
      const newValue = `${before}{{${suggestion.insert}${after}`;
      onChange(newValue);
      setShowMenu(false);

      // Set cursor after the inserted text
      const newCursorPos = before.length + 2 + suggestion.insert.length;
      setTimeout(() => {
        el.setSelectionRange(newCursorPos, newCursorPos);
        el.focus();
      }, 0);
    },
    [value, onChange, getTokenStart, plainMode]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showMenu || filtered.length === 0) {
        // Menu not open – forward Enter to onSubmit if provided
        if (e.key === "Enter" && onSubmit) {
          e.preventDefault();
          onSubmit();
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => (i + 1) % filtered.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertSuggestion(filtered[selectedIdx]);
      } else if (e.key === "Escape") {
        setShowMenu(false);
      }
    },
    [showMenu, filtered, selectedIdx, insertSuggestion, onSubmit]
  );

  // Close menu on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        ref.current &&
        !ref.current.contains(e.target as Node)
      ) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (!showMenu || !menuRef.current) return;
    const item = menuRef.current.children[selectedIdx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIdx, showMenu]);

  const commonProps = {
    value,
    placeholder,
    spellCheck,
    className,
    onKeyDown: handleKeyDown,
    onClick: () => {
      const el = ref.current;
      if (!el) return;
      setTimeout(() => {
        const cursor = el.selectionStart ?? value.length;
        const tokenStart = getTokenStart(value, cursor);
        if (tokenStart !== null) {
          const partial = plainMode ? value : value.slice(tokenStart + 2, cursor);
          setFilter(partial);
          setShowMenu(true);
          setSelectedIdx(0);
        } else {
          setShowMenu(false);
        }
      }, 0);
    },
  };

  return (
    <div className="relative">
      {multiline ? (
        <Textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          {...commonProps}
          onChange={(e) => handleChange(e.target.value)}
        />
      ) : (
        <Input
          ref={ref as React.Ref<HTMLInputElement>}
          {...commonProps}
          onChange={(e) => handleChange(e.target.value)}
        />
      )}
      {showMenu && filtered.length > 0 && (
        <div
          ref={menuRef}
          className="absolute left-0 z-50 mt-1 w-full max-h-[180px] overflow-y-auto rounded-md border bg-popover shadow-md"
        >
          {filtered.map((s, idx) => (
            <button
              key={s.label}
              type="button"
              className={`flex w-full items-start gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                idx === selectedIdx
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-accent/50"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertSuggestion(s);
              }}
              onMouseEnter={() => setSelectedIdx(idx)}
            >
              <code className="font-mono font-medium shrink-0 text-foreground">
                {plainMode ? s.label : `{{${s.label}}}`}
              </code>
              {s.description && (
                <span className="text-muted-foreground truncate">{s.description}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper to build suggestions from prior workflow nodes
// When lastOutputs is provided, extract real paths from execution data
export function buildVariableSuggestions(
  priorNodes: { id: string; type: string }[],
  fieldHints: Record<string, string[]>,
  lastOutputs?: Record<string, unknown>
): Suggestion[] {
  const suggestions: Suggestion[] = [];
  for (const node of priorNodes) {
    if (lastOutputs?.[node.id]) {
      // Use real paths from last execution
      const paths = extractPaths(lastOutputs[node.id]);
      for (const path of paths) {
        suggestions.push({
          label: `${node.id}.${path}`,
          insert: `${node.id}.${path}}}`,
          description: `${node.type} → ${path}`,
        });
      }
      // Add format operator suggestions for array fields (like rows)
      if (node.type === "run_query" || node.type === "loop") {
        for (const fmt of ["table", "list", "csv", "summary"]) {
          suggestions.push({
            label: `${node.id}.rows | ${fmt}`,
            insert: `${node.id}.rows | ${fmt}}}`,
            description: `Format rows as ${fmt}`,
          });
        }
      }
    } else {
      // Fall back to static hints
      const fields = fieldHints[node.type] ?? [];
      for (const field of fields) {
        suggestions.push({
          label: `${node.id}.${field}`,
          insert: `${node.id}.${field}}}`,
          description: `${node.type} → ${field}`,
        });
      }
      // Add format suggestions for query/loop nodes even without execution data
      if (node.type === "run_query" || node.type === "loop") {
        for (const fmt of ["table", "list", "csv", "summary"]) {
          suggestions.push({
            label: `${node.id}.rows | ${fmt}`,
            insert: `${node.id}.rows | ${fmt}}}`,
            description: `Format rows as ${fmt}`,
          });
        }
      }
    }
  }
  return suggestions;
}

// Build plain-mode suggestions from live node outputs (for evaluate expression).
// Returns paths like "run_query_1.rows[0].customer_id" with value previews.
export function buildPlainSuggestions(
  nodeOutputs: Record<string, unknown>
): Suggestion[] {
  const suggestions: Suggestion[] = [];
  for (const [nodeId, output] of Object.entries(nodeOutputs)) {
    if (output === null || output === undefined) continue;
    const paths = extractPaths(output);
    for (const path of paths) {
      const fullPath = `${nodeId}.${path}`;
      // Resolve value for preview
      let val: unknown = output;
      for (const part of path.replace(/\[(\d+)\]/g, ".$1").split(".")) {
        if (val === null || val === undefined || typeof val !== "object") { val = undefined; break; }
        val = Array.isArray(val) ? val[parseInt(part, 10)] : (val as Record<string, unknown>)[part];
      }
      const preview = val === undefined ? "" :
        typeof val === "object" ? JSON.stringify(val).slice(0, 60) : String(val).slice(0, 60);
      suggestions.push({
        label: fullPath,
        insert: fullPath,
        description: preview || undefined,
      });
    }
  }
  return suggestions;
}

// Extract dot-notation paths from a JSON value
function extractPaths(obj: unknown, prefix = "", depth = 0, maxDepth = 3): string[] {
  if (depth >= maxDepth || obj === null || obj === undefined) return [];
  if (typeof obj !== "object") return [];
  const paths: string[] = [];
  if (Array.isArray(obj)) {
    if (obj.length > 0) {
      paths.push(...extractPaths(obj[0], `${prefix}[0]`, depth + 1, maxDepth));
    }
    return paths;
  }
  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    paths.push(fullPath);
    if (val !== null && typeof val === "object") {
      paths.push(...extractPaths(val, fullPath, depth + 1, maxDepth));
    }
  }
  return paths;
}
